import { useState, useCallback } from 'react';
import {HttpMethod, initFetch} from "../utils/fetcher";

// Hook personnalisé pour générer un PDF
const usePdfGenerator = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const generatePdf = useCallback(async (uri, jsonParams, httpMethod) => {
    setLoading(true);
    setError(null);
    try {
      const {url, params} = initFetch(uri, httpMethod);

      // Appel POST avec les paramètres JSON
      const response = httpMethod === HttpMethod.POST
        ? await fetch(url,{...params, body: JSON.stringify(jsonParams)})
        : await fetch(url, params);

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      // Vérifier que la réponse est bien un PDF
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/pdf')) {
        throw new Error('La réponse n\'est pas un fichier PDF');
      }

      // Convertir la réponse en blob et créer une URL pour l'aperçu inline
      const pdfBlob = await response.blob();
      const objectUrl = URL.createObjectURL(pdfBlob);

      setPdfUrl(objectUrl);
      setModalOpen(true);

      return { success: true, url: objectUrl };

    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const closeModal = useCallback(() => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
    setModalOpen(false);
  }, [pdfUrl]);

  // Fonction alternative pour télécharger le PDF au lieu de l'afficher
  const downloadPdf = useCallback(async (url, jsonParams, filename = 'document.pdf') => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(jsonParams)
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const pdfBlob = await response.blob();
      const pdfUrl = URL.createObjectURL(pdfBlob);

      // Créer un lien de téléchargement temporaire
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Nettoyer l'URL
      URL.revokeObjectURL(pdfUrl);

      return { success: true };

    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    generatePdf,
    downloadPdf,
    closeModal,
    pdfUrl,
    modalOpen,
    loading,
    error
  };
};

export default usePdfGenerator;
